import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Download, ExternalLink, Settings } from 'lucide-react'
import { AppHeader } from '@/components/AppHeader'
import { BrandMark } from '@/components/BrandLogo'

/**
 * The address that raises the installed app.
 *
 * <p>Nothing is passed in it. The app is not being told to do anything, only
 * to come forward, so a link that opens it cannot make it act.
 */
const APP_URL = 'nevtan://open'

/** Seconds shown, and waited, before giving up on the app answering. */
const COUNTDOWN_FROM = 4

/**
 * Where signing in on the website leads.
 *
 * <p>The work happens in the desktop app, so the site hands over to it rather
 * than trying to be a second place to do the same thing.
 *
 * <p>A browser cannot ask whether an application is installed -- there is no
 * such API, and probing for one is unreliable and hostile. So this does not
 * guess. It asks the app to come forward, says plainly that it is doing so,
 * and offers the download in the same breath.
 *
 * <p>The wait is visible and it stops the moment the reader does anything.
 * This page is also where a signed-in visitor lands, and the only way from
 * here to their account -- a timer that fires under them while they reach for
 * that link would make the page a race rather than a choice. Someone who sits
 * still is waiting and gets taken onward; someone who moves has other plans.
 */
export function OpenAppPage() {
  const navigate = useNavigate()

  /** Counts down while nobody has intervened; null once the wait is off. */
  const [secondsLeft, setSecondsLeft] = useState<number | null>(COUNTDOWN_FROM)

  // React invokes effects twice in development. Firing the scheme twice would
  // ask the browser to open the app twice over.
  const asked = useRef(false)

  useEffect(() => {
    // Only the ask is guarded, not the whole effect. React runs an effect
    // twice in development -- mount, clean up, mount again -- so returning
    // early on the second pass left the page with no timer at all: the first
    // one had already been cleared. The scheme fires once; the countdown is
    // set up every time.
    if (!asked.current) {
      asked.current = true
      window.location.href = APP_URL
    }

    let cancelled = false

    /**
     * Anything that means the reader is still here and has their own plans.
     *
     * <p>`pointermove` is included deliberately: reaching for a button is the
     * earliest sign of intent there is, and waiting for the click would let
     * the timer fire out from under the cursor on its way there.
     */
    const stop = () => {
      if (cancelled) return
      cancelled = true
      setSecondsLeft(null)
    }

    /**
     * The app coming forward hides this page -- the one signal a browser gives
     * us that something answered. Recorded as it happens rather than sampled
     * at the end: a page can be hidden at that instant for reasons of its own,
     * and reading it once would strand someone here.
     */
    const onHide = () => {
      if (document.visibilityState === 'hidden') stop()
    }

    const events: [EventTarget, string][] = [
      [window, 'pointerdown'],
      [window, 'pointermove'],
      [window, 'keydown'],
      [window, 'wheel'],
      [window, 'focusin'],
      [window, 'pagehide'],
      [document, 'visibilitychange'],
    ]
    for (const [target, name] of events) {
      target.addEventListener(name, name === 'visibilitychange' ? onHide : stop, { passive: true })
    }

    const tick = setInterval(() => {
      setSecondsLeft((left) => {
        if (left === null) return null
        if (left > 1) return left - 1
        // Out of time, and nothing interrupted: nothing answered, so the
        // download is where this was always going.
        navigate('/download', { replace: true })
        return null
      })
    }, 1000)

    return () => {
      clearInterval(tick)
      for (const [target, name] of events) {
        target.removeEventListener(name, name === 'visibilitychange' ? onHide : stop)
      }
    }
  }, [navigate])

  const waiting = secondsLeft !== null

  return (
    // The same masthead the rest of the signed-in app carries, so this screen
    // is not a dead end: the account menu in it is how someone signs out from
    // here, and it keeps the suite switcher and theme toggle within reach.
    <div className="flex min-h-screen flex-col">
      <AppHeader />

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center px-6 text-center">
        <BrandMark alt="" className={`h-14 w-14 ${waiting ? 'animate-pulse' : ''}`} />

        <h1 className="mt-5 text-[1.4rem] font-extrabold tracking-display">
          {waiting ? 'Opening NevTan AI…' : 'NevTan AI runs on your desktop'}
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-content-muted">
          Your models run on this machine, so the work happens in the app rather than in this tab.
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
          <Link
            to="/download"
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-accent-content transition hover:opacity-90"
          >
            <Download size={14} /> Download the app
          </Link>
          <a
            href={APP_URL}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-[13px] font-semibold text-content transition hover:bg-surface-raised"
          >
            <ExternalLink size={14} /> Open the app
          </a>
        </div>

        {/* Said out loud, and only while it is true. A redirect nobody was told
            about is the same surprise whether it takes three seconds or ten. */}
        <p className="mt-4 h-4 text-[12px] text-content-muted">
          {waiting
            ? `Taking you to the download in ${secondsLeft}… move the mouse to stay here.`
            : 'Nothing opened automatically. Use the buttons above, or carry on below.'}
        </p>

        {/* The account pages are still served here, and this is where a
            signed-in visitor lands, so it has to offer a way to reach them. */}
        <Link
          to="/settings"
          className="mt-8 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-accent hover:underline"
        >
          <Settings size={13} /> Manage your account and devices
        </Link>
      </div>
    </div>
  )
}
